import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, DestroyRef, inject, PLATFORM_ID } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { isForcedLightBlankPath } from '@core/boot/public-boot-path.util';
import { BrowserChromeThemeService } from '@core/browser-chrome/browser-chrome-theme.service';
import { AuthSeoService } from '@core/seo/auth-seo.service';
import { AppSettings } from 'src/app/config';
import { CoreService } from 'src/app/services/core.service';
import { MaterialModule } from 'src/app/material.module';

@Component({
  selector: 'app-blank',
  templateUrl: './blank.component.html',
  styleUrls: [],
  imports: [RouterOutlet, MaterialModule, CommonModule],
})
export class BlankComponent {
  private readonly settings = inject(CoreService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly browserChrome = inject(BrowserChromeThemeService);
  private readonly authSeo = inject(AuthSeoService);
  private readonly doc = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly htmlElement = this.doc.documentElement;

  options = this.settings.getOptions();

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.applyThemeForUrl(this.router.url, this.options);
    }
    this.syncBrowserChrome(this.router.url);
    this.syncAuthSeo(this.router.url);

    this.destroyRef.onDestroy(() => this.authSeo.clear());

    this.settings.notify.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((options) => {
      this.options = options;
      this.applyThemeForUrl(this.router.url, options);
    });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.applyThemeForUrl(event.urlAfterRedirects, this.options);
        this.syncBrowserChrome(event.urlAfterRedirects);
        this.syncAuthSeo(event.urlAfterRedirects);
      });
  }

  receiveOptions(options: AppSettings): void {
    this.options = options;
    this.applyThemeForUrl(this.router.url, options);
  }

  private applyThemeForUrl(url: string, options: AppSettings): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const path = url.split('?')[0] ?? '';
    // Auth / marketing / catálogo público: nunca heredar dark del panel (preferencia staff).
    const forceLight = isForcedLightBlankPath(path);
    this.toggleDarkTheme(forceLight ? { ...options, theme: 'light' } : options);
    this.toggleColorsTheme(options);
    this.htmlElement.classList.toggle('cardBorder', !!options.cardBorder);
  }

  toggleDarkTheme(options: AppSettings) {
    if (options.theme === 'dark') {
      this.htmlElement.classList.add('dark-theme');
      this.htmlElement.classList.remove('light-theme');
    } else {
      this.htmlElement.classList.remove('dark-theme');
      this.htmlElement.classList.add('light-theme');
    }
  }

  toggleColorsTheme(options: AppSettings) {
    const classList = this.htmlElement.classList;
    for (let index = classList.length - 1; index >= 0; index -= 1) {
      const className = classList.item(index);
      if (className?.endsWith('_theme')) {
        classList.remove(className);
      }
    }

    classList.add(options.activeTheme);
  }

  private syncBrowserChrome(url: string): void {
    const path = url.split('?')[0] ?? '';
    if (
      path.startsWith('/authentication') ||
      path.startsWith('/legal') ||
      path.startsWith('/website')
    ) {
      this.browserChrome.applyAuth();
      return;
    }
    if (path.startsWith('/onboard')) {
      this.browserChrome.applyOnboard();
      return;
    }
    if (isForcedLightBlankPath(path) && path !== '/' && path !== '') {
      // Landing / tienda / cart / checkout públicos: chrome claro (sin dark de staff).
      this.browserChrome.applyPublicCatalog();
    }
  }

  private syncAuthSeo(url: string): void {
    const path = url.split('?')[0] ?? '';
    if (path.startsWith('/authentication')) {
      this.authSeo.apply();
      return;
    }
    this.authSeo.clear();
  }
}
